'use client';

import { usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import { SyntheticEvent, FC, memo, useState, useEffect, useRef } from 'react';

import { PORTALS } from '@/.constants/portals';
import AiVideosIcon from '@/public/icons/ai_videos.svg';
import { useInView } from '@/src/hooks/useInView';
import { useVitrinaPosition } from '@/src/hooks/useVitrinaPosition';
import { useGTMContext, useStyleContext } from '@/src/providers';

const styles = {
  wrapper: 'flex flex-col gap-3',
  styleItem: 'w-full relative overflow-hidden rounded-lg content-visible',
  preview:
    'w-full h-auto aspect-square object-cover object-top pointer-events-none',
  line: 'flex items-center gap-2',
  lineTitle: 'text-lg font-bold',
  lineIcon: 'w-4 h-4',
};

export const ImageToVideo: FC = memo(() => {
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const t = useTranslations('MenuPage');
  const [hasStarted, setHasStarted] = useState(false);
  const [refForPlay, isInViewport] = useInView<HTMLButtonElement>({
    rootMargin: '0px',
    threshold: 0.1,
  });
  const { openPortal } = usePortalContext();
  const { genderMode, setPrevPortal } = useStyleContext();
  const sendGTM = useGTMContext();
  const videoRef = useRef<HTMLVideoElement>(null);

  const vitrinaPosition = useVitrinaPosition('imageToVideo');

  const handleClickImage = (event: SyntheticEvent) => {
    sendGTM({
      event: 'menu_selection',
      type: null,
      block: 'Анимировать фото',
      category_name: null,
      name: null,
      category_position: null,
      vitrina_position: vitrinaPosition,
      gender: genderMode,
      source: 'Витрина',
    });

    setPrevPortal('main');
    openPortal(PORTALS.AnyImageToVideo);
  };

  useEffect(() => {
    if (!hasStarted && isInViewport && videoRef.current) {
      videoRef.current.play().catch();
      setHasStarted(true);
    }
  }, [isInViewport, hasStarted]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.line}>
        <AiVideosIcon className={styles.lineIcon} />
        <div className={styles.lineTitle}>{t('imageToVideoTitle')}</div>
      </div>
      <button
        ref={refForPlay}
        className={styles.styleItem}
        onClick={handleClickImage}
      >
        {!mediaLoaded && (
          <div className="absolute z-10 inset-0 animate-skeleton-pulse"></div>
        )}
        <video
          ref={videoRef}
          preload="metadata"
          // autoPlay
          loop
          muted
          playsInline
          className={styles.preview}
          onLoadedData={() => setMediaLoaded(true)}
        >
          <source type="video/mp4" src="/images/image_to_video_new.mp4" />
        </video>
      </button>
    </div>
  );
});

ImageToVideo.displayName = 'ImageToVideo';
